/* eslint-disable camelcase */
import { Grid } from '@mui/material'
import AgriAction from 'agriAdministration/actions/AgriAction'
import IntervenantCard from 'exploitations/components/cards/IntervenantCard'
import PropTypes from 'prop-types'
import React, { useEffect, useMemo, useState } from 'react'
import ContactAction from 'referencial/components/contact/actions/ContactAction'
import i18n from 'simple-react-i18n'
import DtoDeclaration from 'survey/dto/DtoDeclaration'
import ToastrAction from 'toastr/actions/ToastrAction'
import { getLogin } from 'utils/SettingUtils'
import SelectContactModal from '../modals/SelectContactModal'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router'

const DeclarationContactsStep = ({ allContributors, readMode, onChangeDeclaration }) => {
    const [openModal, setOpenModal] = useState(false)
    const [contributorToEdit, setContributorToEdit] = useState(false)
    const [contributorTypeEdit, setContributorTypeEdit] = useState(false)

    const {
        contacts,
        exploitation,
        declarationContributors,
        sandreCodes,
        declaration,
    } = useSelector(store => ({
        contacts: store.ContactReducer.contacts,
        exploitation: store.AgriReducer.exploitation,
        declarationContributors: store.AgriReducer.declarationContributors,
        sandreCodes: store.ReferencialReducer.sandreCodes,
        declaration: store.AgriReducer.declaration,
    }), shallowEqual)

    const dispatch = useDispatch()
    const params = useParams()
    const idSurvey = parseInt(params.id)

    useEffect(() => {
        dispatch(AgriAction.fetchDeclarationContributors(idSurvey)).then(() => {
            dispatch(ContactAction.fetchContacts(true)).then(() => {
                dispatch(AgriAction.fetchDeclaration(declaration.idDeclaration))
            })
        })
    }, [])

    const formatContact = (contact, updatedContact) => {
        if (updatedContact) {
            return {
                ...contact,
                ...updatedContact,
                address: updatedContact.road,
                additionalAddress: updatedContact.addressComplement,
                desktopTel: updatedContact.fax,
                postalBox: updatedContact.postalCode,
            }
        }
        return contact
    }

    const onSave = (contributor, type) => {
        const id = contributor.idContributorTemp || contributor.idContributor || contributor.id
        const newLink = {
            contributorType: type,
            surveyId: idSurvey,
            idExploitation: exploitation.idExploitation,
            updateLogin: getLogin(),
        }
        const contribFormatted = {
            ...contributor,
            road: contributor.address,
            addressComplement: contributor.additionalAddress,
            fax: contributor.desktopTel,
            postalCode: contributor.postalBox,
            idContributor: id,
            surveyId: idSurvey,
            contactType: 2,
        }
        // si le contact est déjà lié
        if (id && allContributors.find((cT) => cT.idContributor === id && !!cT.isTemp === !!contributor.idContributorTemp && cT.contributorType === type)) {
            // si c'est un ajout du même type de lien
            if (!contributorTypeEdit) {
                dispatch(ToastrAction.warning(i18n.linkAlreadyExists))
            // sinon, c'est une modification des infos du contact
            } else {
                dispatch(AgriAction.updateDeclarationContributor(idSurvey, contribFormatted))
                setOpenModal(false)
                setContributorToEdit()
                setContributorTypeEdit()
            }
        // si c'est une création de nouveau contact
        } else if (!id) {
            dispatch(AgriAction.updateDeclarationContributor(idSurvey, contribFormatted)).then((idReturned) => {
                onChangeDeclaration('link_contributorTypes', [
                    ...declaration.link_contributorTypes,
                    {
                        ...newLink,
                        idContributor: idReturned,
                        isTemp: true,
                        mode: 'c',
                    },
                ])
            })
            setOpenModal(false)
            setContributorToEdit()
            setContributorTypeEdit()
        // si c'est un rajout de lien non existant
        } else {
            dispatch(AgriAction.updateDeclarationContributor(idSurvey, contribFormatted)).then(() => {
                onChangeDeclaration('link_contributorTypes', [
                    ...(contributorTypeEdit ?
                        declaration.link_contributorTypes.filter((cT) => !(cT.idContributor === id && !!cT.isTemp === !!contributor.idContributorTemp && cT.contributorType === contributorTypeEdit)) :
                        declaration.link_contributorTypes),
                    {
                        ...newLink,
                        idContributor: id,
                        isTemp: !!contributor.idContributorTemp,
                        mode: contributorTypeEdit ? 'u' : 'c',
                    },
                ])
            })
            setOpenModal(false)
            setContributorToEdit()
            setContributorTypeEdit()
        }
    }

    const onDelete = () => {
        const id = contributorToEdit.idContributorTemp || contributorToEdit.idContributor || contributorToEdit.id
        onChangeDeclaration('link_contributorTypes', [
            ...declaration.link_contributorTypes.filter((cT) => !(cT.idContributor === id && !!cT.isTemp === !!contributorToEdit.idContributorTemp && cT.contributorType === contributorTypeEdit)),
            {
                contributorType: contributorTypeEdit,
                surveyId: idSurvey,
                idExploitation: exploitation.idExploitation,
                updateLogin: getLogin(),
                idContributor: id,
                isTemp: !!contributorToEdit.idContributorTemp,
                mode: 'd',
            },
        ])
        setOpenModal(false)
        setContributorToEdit()
        setContributorTypeEdit()
    }

    const onCancel = () => {
        setOpenModal(false)
        setContributorToEdit()
        setContributorTypeEdit()
    }

    const listContributors = useMemo(() => {
        const typesContributors = [{ code: -1, name: i18n.legalRepresentative }, ...sandreCodes.filter((c) => c.field === 'EXPLOITATIONS.TYPEINTERVENANT')]
        return allContributors.map((cT) => {
            const contact = cT.isTemp ? {} : (contacts.find((c) => c.id === cT.idContributor) || {})
            const contactUpdated = declarationContributors.find((c) => (cT.isTemp ? c.idContributorTemp : c.idContributor) === cT.idContributor && c.contactType === 2)
            const formattedContact = formatContact(contact, contactUpdated)
            const type = typesContributors.find((t) => t.code === cT.contributorType)
            return (
                <Grid key={cT.idContributor} size={4}>
                    <IntervenantCard
                        intervenant={formattedContact}
                        title={type ? type.name : i18n.unknown}
                        actions={readMode ?
                            []
                            :
                            [
                                {
                                    onClick: () => {
                                        setOpenModal(true)
                                        setContributorToEdit(formattedContact)
                                        setContributorTypeEdit(cT.contributorType)
                                    },
                                    icon: 'edit',
                                    color: 'white',
                                },
                            ]
                        }
                    />
                </Grid>
            )
        })
    }, [allContributors])

    const showLegalRepresentative = () => {
        return !allContributors.find((c) => c.mode !== 'd' && c.contributorType === -1)
    }

    return (
        <Grid container spacing={2} >
            {listContributors}
            {openModal && (
                <SelectContactModal
                    showLegalRepresentative={showLegalRepresentative}
                    contributor={contributorToEdit}
                    contributorType={contributorTypeEdit}
                    open={openModal}
                    onSave={onSave}
                    onDelete={onDelete}
                    onCancel={onCancel}
                />
            )}
        </Grid>
    )
}

DeclarationContactsStep.propTypes = {
    allContributors: PropTypes.arrayOf().isRequired,
    declaration: PropTypes.instanceOf(DtoDeclaration).isRequired,
    onChangeDeclaration: PropTypes.func.isRequired,
    dataLoaded: PropTypes.bool,
    readMode: PropTypes.bool,
}

export default DeclarationContactsStep
